import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import API from "@/lib/api";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, CalendarDays, Check, X as XIcon, MessageSquare,
  UserPlus, Pencil, Download, Eye, FileText, Mail,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

function formatLeaveDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleDateString();
  } catch {
    return "—";
  }
}

// ─── Constants ─────────────────────────────────────────────────────────

const REJECTION_REASONS = [
  "Not enough leave days",
  "Late notice",
  "Insufficient documentation",
  "Operational requirements",
  "Conflict with scheduled work",
  "Other (custom reason)",
];

const ALL_LEAVE_TYPES = [
  "Annual Leave",
  "Sick Leave",
  "Compassionate Leave",
  "Maternity Leave",
  "Paternity Leave",
  "Study Leave",
];

function getLeaveTypes(gender) {
  return ALL_LEAVE_TYPES.filter((t) => {
    if (t === "Maternity Leave") return gender === "female";
    if (t === "Paternity Leave") return gender === "male";
    return true;
  });
}

const statusColors = {
  pending:   "bg-[#FFFBEB] text-[#92400E] border-[#F59E0B]",
  approved:  "bg-[#ECFDF5] text-[#065F46] border-[#10B981]",
  rejected:  "bg-[#FEF2F2] text-[#991B1B] border-[#EF4444]",
  more_info: "bg-[#EFF6FF] text-[#1E40AF] border-[#3B82F6]",
};

// ─── Attachment Viewer Modal ──────────────────────────────────────────
// Shows the full-size image with navigation for multiple files.

function AttachmentViewer({ leaveId, attachments, currentIndex, onClose }) {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [index, setIndex] = useState(currentIndex || 0);
  const [contentUrl, setContentUrl] = useState(null);
  const [downloadBlob, setDownloadBlob] = useState(null);

  const totalFiles = attachments?.length || 1;
  const currentFile = attachments?.[index] || { filename: "attachment.pdf" };

  const backendOrigin = process.env.REACT_APP_BACKEND_URL ? process.env.REACT_APP_BACKEND_URL.replace(/\/$/, "") : "";
  const previewUrl = `${backendOrigin}/api/leave/${leaveId}/preview?index=${index}`;
  const attachmentUrl = `${backendOrigin}/api/leave/${leaveId}/attachment?index=${index}`;

  useEffect(() => {
    let isMounted = true;
    if (!leaveId) return;

    const fetchBlob = async () => {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem("auth_token");
      try {
        const res = await fetch(previewUrl, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        if (!isMounted) return;
        if (!res.ok) {
          const message = await res.text();
          throw new Error(message || `Unable to load attachment (${res.status})`);
        }
        const blob = await res.blob();
        if (!isMounted) return;
        const url = URL.createObjectURL(blob);
        setDownloadBlob(blob);
        setContentUrl(url);
        setLoading(false);
      } catch (err) {
        if (!isMounted) return;
        setError(err.message || "Unable to load attachment.");
        setLoading(false);
      }
    };

    fetchBlob();

    return () => {
      isMounted = false;
      if (contentUrl) {
        URL.revokeObjectURL(contentUrl);
      }
    };
  }, [previewUrl, leaveId]);

  const handleDownload = async () => {
    const token = localStorage.getItem("auth_token");
    try {
      const blob = downloadBlob || await (async () => {
        const res = await fetch(attachmentUrl, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        if (!res.ok) {
          const message = await res.text();
          throw new Error(message || `Unable to download attachment (${res.status})`);
        }
        return res.blob();
      })();

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = currentFile.filename || "attachment.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || "Failed to download attachment.");
    }
  };

  const handlePrev = () => {
    if (index > 0) {
      setLoading(true);
      setError(null);
      setIndex(index - 1);
    }
  };

  const handleNext = () => {
    if (index < totalFiles - 1) {
      setLoading(true);
      setError(null);
      setIndex(index + 1);
    }
  };

  return (
    <Dialog open={true} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-3xl overflow-hidden flex flex-col" style={{ maxHeight: "90vh" }}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-sm">
            <FileText className="h-4 w-4 text-slate-500" />
            <span className="truncate">{currentFile.filename || "Attachment"}</span>
            {totalFiles > 1 && (
              <span className="text-xs text-slate-400 ml-auto">
                {index + 1} / {totalFiles}
              </span>
            )}
          </DialogTitle>
          <DialogDescription className="sr-only">Attachment viewer</DialogDescription>
        </DialogHeader>

        <div
          className="flex-1 overflow-auto bg-slate-100 rounded-sm flex items-center justify-center p-2 relative"
          style={{ minHeight: 0 }}
        >
          {loading && !error && (
            <div className="absolute flex flex-col items-center gap-2 z-10">
              <div className="h-8 w-8 border-[3px] border-slate-300 border-t-[#002FA7] rounded-full animate-spin" />
              <p className="text-xs text-slate-500">Loading…</p>
            </div>
          )}

          {error ? (
            <div className="flex flex-col items-center gap-3 py-16 px-6 text-center">
              <FileText className="h-10 w-10 text-slate-300" />
              <p className="text-sm font-medium text-slate-600">Preview unavailable</p>
              <p className="text-xs text-slate-400">{error}</p>
              <button
                onClick={handleDownload}
                className="mt-2 flex items-center gap-1.5 text-xs bg-[#002FA7] text-white rounded-sm px-3 py-1.5 font-medium hover:bg-[#002480] transition-colors"
              >
                <Download className="h-3.5 w-3.5" /> Download instead
              </button>
            </div>
          ) : contentUrl ? (
            <iframe
              key={contentUrl}
              src={contentUrl}
              title={currentFile.filename || "Attachment"}
              className="w-full h-full rounded-sm shadow-sm border-0"
              style={{ display: loading ? "none" : "block" }}
              onLoad={() => setLoading(false)}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-slate-500">
              {loading ? "Preparing preview…" : "No preview available."}
            </div>
          )}

          {/* Navigation arrows for multiple files */}
          {totalFiles > 1 && (
            <>
              {index > 0 && (
                <button
                  onClick={handlePrev}
                  className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow-sm border border-slate-200 transition-colors z-20"
                  title="Previous"
                >
                  <svg className="h-5 w-5 text-slate-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              )}
              {index < totalFiles - 1 && (
                <button
                  onClick={handleNext}
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow-sm border border-slate-200 transition-colors z-20"
                  title="Next"
                >
                  <svg className="h-5 w-5 text-slate-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              )}
            </>
          )}
        </div>

        <DialogFooter className="pt-3 flex-shrink-0">
          <button
            onClick={handleDownload}
            className="flex items-center gap-1.5 text-xs bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-sm px-3 py-1.5 font-medium transition-colors"
          >
            <Download className="h-3.5 w-3.5" /> Download
          </button>
          <button
            onClick={onClose}
            className="flex items-center gap-1.5 text-xs bg-slate-800 hover:bg-slate-900 text-white rounded-sm px-3 py-1.5 font-medium transition-colors"
          >
            <XIcon className="h-3.5 w-3.5" /> Close
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────

export default function LeavePage() {
  const { user } = useAuth();

  const [leaves, setLeaves]                   = useState([]);
  const [teamLeaves, setTeamLeaves]           = useState([]);
  const [balances, setBalances]               = useState(null);
  const [employees, setEmployees]             = useState([]);
  const [loading, setLoading]                 = useState(true);
  const [activeTab, setActiveTab]             = useState("my");
  const [employeeGender, setEmployeeGender]   = useState("");

  // Apply dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm]             = useState({ leave_type: "Annual Leave", start_date: "", end_date: "", reason: "" });
  const [attachments, setAttachments] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Assign dialog (manager only)
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignForm, setAssignForm] = useState({ employee_id: "", leave_type: "Annual Leave", start_date: "", end_date: "", reason: "" });
  const [assigning, setAssigning]   = useState(false);

  // Edit dialog (employee only)
  const [editOpen, setEditOpen]     = useState(false);
  const [editLeave, setEditLeave]   = useState(null);
  const [editForm, setEditForm]     = useState({ leave_type: "", start_date: "", end_date: "", reason: "" });
  const [editAttachments, setEditAttachments] = useState([]);
  const [editing, setEditing]       = useState(false);

  const isManager  = ["admin", "hr_manager"].includes(user?.role);
  const LEAVE_TYPES = getLeaveTypes(employeeGender);

  // ── Fetches ────────────────────────────────────────────────────────────

  const fetchAll = useCallback(() => {
    setLoading(true);
    API.get("/leave").then(({ data }) => setLeaves(data)).catch(console.error).finally(() => setLoading(false));
    API.get("/leave/team").then(({ data }) => setTeamLeaves(data)).catch(() => {});
    API.get("/leave/balances").then(({ data }) => setBalances(data?.balances || null)).catch(console.error);
  }, []);

  useEffect(() => {
    fetchAll();
    API.get("/employees/me/profile").then(({ data }) => { if (data?.gender) setEmployeeGender(data.gender); }).catch(() => {});
    if (isManager) API.get("/employees").then(({ data }) => setEmployees(data)).catch(console.error);
  }, []);

  // ── Handlers ───────────────────────────────────────────────────────────

  const handleApply = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("leave_type", form.leave_type);
      fd.append("start_date", form.start_date);
      fd.append("end_date",   form.end_date);
      fd.append("reason",     form.reason);
      // Append each file individually — FastAPI receives them as List[UploadFile]
      for (const file of attachments) {
        fd.append("attachments", file);
      }
      await API.post("/leave", fd);
      toast.success("Leave request submitted");
      setDialogOpen(false);
      setForm({ leave_type: "Annual Leave", start_date: "", end_date: "", reason: "" });
      setAttachments([]);
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  const handleAssign = async (e) => {
    e.preventDefault();
    setAssigning(true);
    try {
      await API.post("/leave/assign", { ...assignForm, auto_approve: true });
      toast.success("Leave assigned successfully");
      setAssignOpen(false);
      setAssignForm({ employee_id: "", leave_type: "Annual Leave", start_date: "", end_date: "", reason: "" });
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setAssigning(false); }
  };

  const handleAction = async (id, status, comment = "") => {
    try {
      await API.put(`/leave/${id}`, { status, comment });
      toast.success(`Leave ${status}`);
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      throw err;
    }
  };

  const handleAmend = async (id, start_date, end_date, comment) => {
    try {
      await API.put(`/leave/${id}/amend`, { start_date, end_date, comment });
      toast.success("Leave amended successfully");
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      throw err;
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!editLeave) return;
    setEditing(true);
    try {
      const fd = new FormData();
      if (editForm.leave_type) fd.append("leave_type", editForm.leave_type);
      if (editForm.start_date) fd.append("start_date", editForm.start_date);
      if (editForm.end_date) fd.append("end_date", editForm.end_date);
      if (editForm.reason !== undefined) fd.append("reason", editForm.reason);
      for (const file of editAttachments) {
        fd.append("attachments", file);
      }
      await API.put(`/leave/${editLeave.id}/edit`, fd);
      toast.success("Leave request updated successfully");
      setEditOpen(false);
      setEditLeave(null);
      setEditForm({ leave_type: "", start_date: "", end_date: "", reason: "" });
      setEditAttachments([]);
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setEditing(false); }
  };

  const handleRedact = async (id) => {
    try {
      await API.put(`/leave/${id}/redact`);
      toast.success("Leave request redacted successfully");
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const handleResendNotification = async (id) => {
    try {
      await API.post(`/leave/${id}/resend-notification`);
      toast.success("Leave notification resent");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const openEditDialog = (leave) => {
    setEditLeave(leave);
    setEditForm({
      leave_type: leave.leave_type,
      start_date: leave.start_date,
      end_date: leave.end_date,
      reason: leave.reason || "",
    });
    setEditAttachments([]);
    setEditOpen(true);
  };

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6" data-testid="leave-page">

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl sm:text-3xl tracking-tight font-medium text-slate-950" style={{ fontFamily: "Outfit, sans-serif" }}>
          Leave Management
        </h1>
        <div className="flex items-center gap-2">
          {isManager && (
            <button onClick={() => setAssignOpen(true)}
              className="bg-slate-800 hover:bg-slate-900 text-white rounded-sm px-4 py-2.5 font-medium transition-colors text-sm flex items-center gap-2">
              <UserPlus className="h-4 w-4" /> Assign Leave
            </button>
          )}
          <button data-testid="apply-leave-button" onClick={() => setDialogOpen(true)}
            className="bg-[#002FA7] hover:bg-[#002480] text-white rounded-sm px-5 py-2.5 font-medium transition-colors text-sm flex items-center gap-2">
            <Plus className="h-4 w-4" /> Apply Leave
          </button>
        </div>
      </div>

      {/* Leave Balance Cards */}
      {balances && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="leave-balances">
          {Object.entries(balances)
            .filter(([type]) => {
              if (!employeeGender) return true;
              if (type === "Maternity Leave" && employeeGender !== "female") return false;
              if (type === "Paternity Leave" && employeeGender !== "male") return false;
              return true;
            })
            .sort(([a], [b]) => { if (a === "Annual Leave") return -1; if (b === "Annual Leave") return 1; return a.localeCompare(b); })
            .map(([type, bal]) => {
              const pct = bal.total > 0 ? (bal.used / bal.total) * 100 : 0;
              return (
                <div key={type} className="bg-white border border-slate-200 rounded-sm p-4 flex flex-col">
                  <div className="text-xs font-semibold text-slate-500 mb-2 truncate" title={type}>{type.replace(" Leave", "")}</div>
                  <div className="text-xl font-medium text-slate-950" style={{ fontFamily: "Outfit, sans-serif" }}>
                    {bal.remaining}<span className="text-sm text-slate-400 font-normal">/{bal.total}</span>
                  </div>
                  <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: pct > 80 ? "#EF4444" : pct > 50 ? "#F59E0B" : "#002FA7" }} />
                  </div>
                  <div className="mt-1 text-xs text-slate-500">{bal.used} used</div>
                  {type === "Annual Leave" && (
                    <div className="mt-auto pt-2 border-t border-slate-100 text-[10px] text-slate-400 space-y-0.5">
                      <div>{bal.accrued_this_year || 0} accrued this year</div>
                      <div>{bal.carryover_from_previous_year || 0} carried over</div>
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="my" data-testid="tab-my-leaves">My Leaves</TabsTrigger>
          {(isManager || teamLeaves.length > 0) && (
            <TabsTrigger value="team" data-testid="tab-team-leaves">
              Team Approvals
              {teamLeaves.filter((l) => l.status === "pending").length > 0 && (
                <span className="ml-1.5 h-5 min-w-5 rounded-full bg-[#002FA7] text-white text-xs flex items-center justify-center px-1">
                  {teamLeaves.filter((l) => l.status === "pending").length}
                </span>
              )}
            </TabsTrigger>
          )}
          {isManager && <TabsTrigger value="all" data-testid="tab-all-leaves">All Leaves</TabsTrigger>}
        </TabsList>

        <TabsContent value="my">
          <LeaveTable leaves={leaves.filter((l) => l.user_email === user?.email)} loading={loading}
            showEmployee={false} showApprover={true} canApprove={false} canAmend={false} canEdit={true}
            onAction={handleAction} onAmend={handleAmend} onEdit={openEditDialog} onRedact={handleRedact} onResend={handleResendNotification} />
        </TabsContent>
        <TabsContent value="team">
          <LeaveTable leaves={teamLeaves} loading={false}
            showEmployee={true} showApprover={false} canApprove={true} canAmend={true} canEdit={false}
            onAction={handleAction} onAmend={handleAmend} onEdit={openEditDialog} onRedact={handleRedact} />
        </TabsContent>
        {isManager && (
          <TabsContent value="all">
            <LeaveTable leaves={leaves} loading={loading}
              showEmployee={true} showApprover={true} canApprove={true} canAmend={true} canEdit={false}
              onAction={handleAction} onAmend={handleAmend} onEdit={openEditDialog} />
          </TabsContent>
        )}
      </Tabs>

      {/* ── Apply Leave Dialog ── */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Apply for Leave</DialogTitle>
            <DialogDescription>Submit a leave request for approval.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleApply} data-testid="leave-form">
            <div className="space-y-4 py-4">
              <div>
                <label className="text-xs font-semibold text-slate-500 block mb-1.5">Leave Type</label>
                <Select value={form.leave_type} onValueChange={(v) => setForm({ ...form, leave_type: v })}>
                  <SelectTrigger data-testid="leave-type-select" className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>{LEAVE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-slate-500 block mb-1.5">Start Date</label>
                  <input data-testid="leave-start-date" type="date" value={form.start_date}
                    onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                    className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7] transition-all text-sm" required />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500 block mb-1.5">End Date</label>
                  <input data-testid="leave-end-date" type="date" value={form.end_date}
                    onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                    className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7] transition-all text-sm" required />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 block mb-1.5">Reason</label>
                <textarea data-testid="leave-reason-input" value={form.reason}
                  onChange={(e) => setForm({ ...form, reason: e.target.value })}
                  className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7] transition-all text-sm resize-none"
                  rows={3} placeholder="Optional reason…" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 block mb-1.5">
                  Attachments
                  {["Sick Leave","Maternity Leave","Paternity Leave","Compassionate Leave","Study Leave"].includes(form.leave_type) && (
                    <span className="text-red-500 ml-1">*</span>
                  )}
                </label>
                <input type="file" accept="image/png,image/jpeg" multiple
                  onChange={(e) => setAttachments(Array.from(e.target.files || []))}
                  className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-full text-sm"
                  required={["Sick Leave","Maternity Leave","Paternity Leave","Compassionate Leave","Study Leave"].includes(form.leave_type) && attachments.length === 0} />
                <p className="text-xs text-slate-500 mt-1">PNG or JPEG (max 5MB each, multiple files allowed)</p>
                {attachments.length > 0 && (
                  <div className="mt-1 space-y-0.5">
                    {attachments.map((f, i) => (
                      <p key={i} className="text-xs text-slate-700 flex items-center gap-1">
                        <FileText className="h-3 w-3 text-slate-400" />
                        {f.name}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <button type="button" onClick={() => setDialogOpen(false)}
                className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-900 rounded-sm px-5 py-2.5 font-medium transition-colors text-sm">Cancel</button>
              <button data-testid="leave-form-submit" type="submit" disabled={submitting}
                className="bg-[#002FA7] hover:bg-[#002480] text-white rounded-sm px-5 py-2.5 font-medium transition-colors text-sm">
                {submitting ? "Submitting…" : "Submit"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Assign Leave Dialog ── */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><UserPlus className="h-5 w-5 text-slate-700" /> Assign Leave to Employee</DialogTitle>
            <DialogDescription>Assign leave directly — auto-approved, employee is notified by email.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAssign}>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-xs font-semibold text-slate-500 block mb-1.5">Employee</label>
                <Select value={assignForm.employee_id} onValueChange={(v) => setAssignForm({ ...assignForm, employee_id: v })}>
                  <SelectTrigger className="h-9"><SelectValue placeholder="Select employee…" /></SelectTrigger>
                  <SelectContent>
                    {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} — {e.department || e.job_title || e.email}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 block mb-1.5">Leave Type</label>
                <Select value={assignForm.leave_type} onValueChange={(v) => setAssignForm({ ...assignForm, leave_type: v })}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>{ALL_LEAVE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-slate-500 block mb-1.5">Start Date</label>
                  <input type="date" value={assignForm.start_date} onChange={(e) => setAssignForm({ ...assignForm, start_date: e.target.value })}
                    className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7] transition-all text-sm" required />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500 block mb-1.5">End Date</label>
                  <input type="date" value={assignForm.end_date} onChange={(e) => setAssignForm({ ...assignForm, end_date: e.target.value })}
                    className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7] transition-all text-sm" required />
                </div>
              </div>
              {assignForm.start_date && assignForm.end_date && (
                <p className="text-xs text-slate-500 -mt-2">
                  {Math.max(0, (new Date(assignForm.end_date) - new Date(assignForm.start_date)) / 86400000 + 1)} day(s)
                </p>
              )}
              <div>
                <label className="text-xs font-semibold text-slate-500 block mb-1.5">Note <span className="font-normal text-slate-400">(optional)</span></label>
                <textarea value={assignForm.reason} onChange={(e) => setAssignForm({ ...assignForm, reason: e.target.value })}
                  className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7] transition-all text-sm resize-none"
                  rows={2} placeholder="Optional note to the employee…" />
              </div>
              <div className="bg-blue-50 border border-blue-100 rounded-sm px-3 py-2 text-xs text-blue-700">
                This leave will be <strong>auto-approved</strong> and the employee will receive an email notification.
              </div>
            </div>
            <DialogFooter>
              <button type="button" onClick={() => setAssignOpen(false)}
                className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-900 rounded-sm px-5 py-2.5 font-medium transition-colors text-sm">Cancel</button>
              <button type="submit" disabled={assigning || !assignForm.employee_id}
                className="bg-slate-800 hover:bg-slate-900 text-white rounded-sm px-5 py-2.5 font-medium transition-colors text-sm flex items-center gap-2">
                <UserPlus className="h-4 w-4" />{assigning ? "Assigning…" : "Assign Leave"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Edit Leave Dialog (Employee Only) ── */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Pencil className="h-5 w-5 text-slate-700" /> Edit Leave Request</DialogTitle>
            <DialogDescription>Update your pending leave request details and attachments.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEdit}>
            <div className="space-y-4 py-4">
              {editLeave && (
                <div className="bg-slate-50 border border-slate-200 rounded-sm px-3 py-2.5 text-xs text-slate-600 space-y-1">
                  <div><span className="font-semibold">Status:</span> {editLeave.status}</div>
                  <div><span className="font-semibold">Applied:</span> {formatLeaveDate(editLeave.applied_at || editLeave.created_at)}</div>
                  <div><span className="font-semibold">Last re-applied / amended:</span> {formatLeaveDate(editLeave.amended_at || editLeave.edited_by_employee_at || editLeave.updated_at || editLeave.created_at)}</div>
                </div>
              )}
              <div>
                <label className="text-xs font-semibold text-slate-500 block mb-1.5">Leave Type</label>
                <Select value={editForm.leave_type} onValueChange={(v) => setEditForm({ ...editForm, leave_type: v })}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>{ALL_LEAVE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-slate-500 block mb-1.5">Start Date</label>
                  <input type="date" value={editForm.start_date}
                    onChange={(e) => setEditForm({ ...editForm, start_date: e.target.value })}
                    className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7] transition-all text-sm" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500 block mb-1.5">End Date</label>
                  <input type="date" value={editForm.end_date}
                    onChange={(e) => setEditForm({ ...editForm, end_date: e.target.value })}
                    className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7] transition-all text-sm" />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 block mb-1.5">Reason</label>
                <textarea value={editForm.reason}
                  onChange={(e) => setEditForm({ ...editForm, reason: e.target.value })}
                  className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7] transition-all text-sm resize-none"
                  rows={2} placeholder="Update reason (optional)…" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 block mb-1.5">
                  Attachments <span className="font-normal text-slate-400">(leave empty to keep current)</span>
                </label>
                <input type="file" accept="image/png,image/jpeg" multiple
                  onChange={(e) => setEditAttachments(Array.from(e.target.files || []))}
                  className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-full text-sm" />
                <p className="text-xs text-slate-500 mt-1">PNG or JPEG (max 5MB each). Upload new files to replace existing ones.</p>
                {editLeave?.attachments?.length > 0 && editAttachments.length === 0 && (
                  <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-sm text-xs text-blue-700">
                    <strong>Current attachments:</strong> {editLeave.attachments.length} file(s)
                  </div>
                )}
                {editAttachments.length > 0 && (
                  <div className="mt-1 space-y-0.5">
                    <p className="text-xs font-semibold text-slate-600">New files to upload:</p>
                    {editAttachments.map((f, i) => (
                      <p key={i} className="text-xs text-slate-700 flex items-center gap-1">
                        <FileText className="h-3 w-3 text-slate-400" />
                        {f.name}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <button type="button" onClick={() => setEditOpen(false)}
                className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-900 rounded-sm px-5 py-2.5 font-medium transition-colors text-sm">Cancel</button>
              <button type="submit" disabled={editing}
                className="bg-[#002FA7] hover:bg-[#002480] text-white rounded-sm px-5 py-2.5 font-medium transition-colors text-sm flex items-center gap-2">
                <Pencil className="h-4 w-4" />{editing ? "Updating…" : "Update Request"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Leave Table ────────────────────────────────────────────────────────

function LeaveTable({ leaves, loading, showEmployee, showApprover, canApprove, canAmend, canEdit, onAction, onAmend, onEdit, onRedact, onResend }) {

  const [rejectOpen, setRejectOpen]             = useState(false);
  const [rejectLeaveId, setRejectLeaveId]       = useState(null);
  const [rejectReason, setRejectReason]         = useState("");
  const [rejectCustom, setRejectCustom]         = useState("");
  const [rejectSubmitting, setRejectSubmitting] = useState(false);

  const [amendOpen, setAmendOpen]               = useState(false);
  const [amendLeave, setAmendLeave]             = useState(null);
  const [amendStart, setAmendStart]             = useState("");
  const [amendEnd, setAmendEnd]                 = useState("");
  const [amendComment, setAmendComment]         = useState("");
  const [amendSubmitting, setAmendSubmitting]   = useState(false);

  // PDF preview state — stored at table level so it layers over the whole page
  const [previewLeave, setPreviewLeave]         = useState(null); // { id, filename }

  const openRejectDialog = (id) => { setRejectLeaveId(id); setRejectReason(""); setRejectCustom(""); setRejectOpen(true); };
  const openAmendDialog  = (l)  => { setAmendLeave(l); setAmendStart(l.start_date); setAmendEnd(l.end_date); setAmendComment(""); setAmendOpen(true); };

  const handleReject = async () => {
    const finalReason = rejectReason === "Other (custom reason)" ? rejectCustom : rejectReason;
    if (!finalReason.trim()) return;
    setRejectSubmitting(true);
    try { await onAction(rejectLeaveId, "rejected", finalReason); setRejectOpen(false); }
    catch (_) {} finally { setRejectSubmitting(false); }
  };

  const handleAmendSubmit = async () => {
    if (!amendStart || !amendEnd) return;
    setAmendSubmitting(true);
    try { await onAmend(amendLeave.id, amendStart, amendEnd, amendComment); setAmendOpen(false); }
    catch (_) {} finally { setAmendSubmitting(false); }
  };

  const amendedDays = amendStart && amendEnd
    ? Math.max(0, (new Date(amendEnd) - new Date(amendStart)) / 86400000 + 1) : 0;

  const colCount = 5 + (showEmployee ? 1 : 0) + (showApprover ? 1 : 0);

  return (
    <>
      {/* Full-viewport attachment viewer */}
      {previewLeave && (
        <AttachmentViewer
          leaveId={previewLeave.id}
          attachments={previewLeave.attachments}
          currentIndex={previewLeave.index || 0}
          onClose={() => setPreviewLeave(null)}
        />
      )}

      <div className="border border-slate-200 rounded-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              {showEmployee && <TableHead className="px-4 py-3 text-xs font-medium text-slate-500">Employee</TableHead>}
              <TableHead className="px-4 py-3 text-xs font-medium text-slate-500">Type</TableHead>
              <TableHead className="px-4 py-3 text-xs font-medium text-slate-500 hidden sm:table-cell">From</TableHead>
              <TableHead className="px-4 py-3 text-xs font-medium text-slate-500 hidden sm:table-cell">To</TableHead>
              <TableHead className="px-4 py-3 text-xs font-medium text-slate-500 hidden md:table-cell">Days</TableHead>
              {showApprover && <TableHead className="px-4 py-3 text-xs font-medium text-slate-500 hidden lg:table-cell">Approver</TableHead>}
              <TableHead className="px-4 py-3 text-xs font-medium text-slate-500">Status</TableHead>
              <TableHead className="px-4 py-3 text-xs font-medium text-slate-500 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={colCount} className="text-center py-8 text-sm text-slate-500">Loading…</TableCell></TableRow>
            ) : leaves.length === 0 ? (
              <TableRow>
                <TableCell colSpan={colCount} className="text-center py-8">
                  <CalendarDays className="h-8 w-8 text-slate-300 mx-auto mb-3" />
                  <p className="text-sm text-slate-500">No leave requests</p>
                </TableCell>
              </TableRow>
            ) : (
              leaves.map((l) => (
                <TableRow key={l.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`leave-row-${l.id}`}>
                  {showEmployee && (
                    <TableCell className="px-4 py-3">
                      <div className="text-sm font-medium text-slate-900">{l.user_name}</div>
                      <div className="text-xs text-slate-500">{l.user_email}</div>
                    </TableCell>
                  )}
                  <TableCell className="px-4 py-3">
                    <div className="text-sm text-slate-700">{l.leave_type}</div>
                    <div className="text-[10px] text-slate-400 mt-1 space-y-0.5">
                      <div>Applied: {formatLeaveDate(l.applied_at || l.created_at)}</div>
                      {(l.amended_at || l.edited_by_employee_at || l.updated_at) &&
                        (l.amended_at || l.edited_by_employee_at || l.updated_at) !== (l.applied_at || l.created_at) && (
                          <div>Re-applied / amended: {formatLeaveDate(l.amended_at || l.edited_by_employee_at || l.updated_at)}</div>
                        )}
                    </div>
                    {l.assigned_by_manager && (
                      <div className="text-[10px] text-slate-400 flex items-center gap-0.5 mt-0.5">
                        <UserPlus className="h-2.5 w-2.5" /> Manager assigned
                      </div>
                    )}
                    {l.amended_by && (
                      <div className="text-[10px] text-blue-500 flex items-center gap-0.5 mt-0.5">
                        <Pencil className="h-2.5 w-2.5" /> Amended
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="px-4 py-3 text-sm text-slate-700 hidden sm:table-cell">{l.start_date}</TableCell>
                  <TableCell className="px-4 py-3 text-sm text-slate-700 hidden sm:table-cell">{l.end_date}</TableCell>
                  <TableCell className="px-4 py-3 text-sm text-slate-700 hidden md:table-cell">{l.days || "—"}</TableCell>
                  {showApprover && (
                    <TableCell className="px-4 py-3 hidden lg:table-cell">
                      {l.approver_name ? <span className="text-xs text-slate-900">{l.approver_name}</span> : <span className="text-xs text-slate-400">No manager</span>}
                    </TableCell>
                  )}
                  <TableCell className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-medium border ${statusColors[l.status] || ""}`}>
                      {l.status}
                    </span>
                    {l.reviewed_by && l.status !== "pending" && <div className="text-xs text-slate-400 mt-0.5">by {l.reviewed_by}</div>}
                    {l.status === "rejected" && l.comment && (
                      <div className="text-xs text-red-500 mt-0.5 max-w-[150px] truncate" title={l.comment}>{l.comment}</div>
                    )}
                  </TableCell>
                  <TableCell className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">

                      {/* 👁 Attachment preview — eye icon, visible to anyone who can see this row */}
                      {(l.attachments?.length > 0 || l.attachment?.filename) && (
                        <button
                          onClick={() => setPreviewLeave({
                            id: l.id,
                            attachments: l.attachments || (l.attachment ? [l.attachment] : []),
                            index: 0,
                          })}
                          className="p-1.5 hover:bg-indigo-50 rounded-sm transition-colors text-slate-400 hover:text-indigo-600"
                          title="View attachments"
                        >
                          <Eye className="h-3.5 w-3.5" />
                        </button>
                      )}

                      {/* ✓ Approve / ✗ Reject */}
                      {canApprove && l.status === "pending" && (
                        <>
                          <button data-testid={`approve-leave-${l.id}`} onClick={() => onAction(l.id, "approved")}
                            className="p-1.5 hover:bg-green-50 rounded-sm transition-colors text-slate-500 hover:text-green-600" title="Approve">
                            <Check className="h-3.5 w-3.5" />
                          </button>
                          <button data-testid={`reject-leave-${l.id}`} onClick={() => openRejectDialog(l.id)}
                            className="p-1.5 hover:bg-red-50 rounded-sm transition-colors text-slate-500 hover:text-red-600" title="Reject">
                            <XIcon className="h-3.5 w-3.5" />
                          </button>
                        </>
                      )}

                      {/* ✏ Amend */}
                      {canAmend && (l.status === "approved" || l.status === "pending") && (
                        <button onClick={() => openAmendDialog(l)}
                          className="p-1.5 hover:bg-blue-50 rounded-sm transition-colors text-slate-500 hover:text-blue-600" title="Amend dates">
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                      )}

                      {/* ✏ Edit (employee only, pending leaves) */}
                      {canEdit && l.status === "pending" && (
                        <button onClick={() => onEdit && onEdit(l)}
                          className="p-1.5 hover:bg-amber-50 rounded-sm transition-colors text-slate-500 hover:text-amber-600" title="Edit request">
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                      )}
                      {canEdit && onResend && (
                        <button onClick={() => onResend(l.id)}
                          className="p-1.5 hover:bg-blue-50 rounded-sm transition-colors text-slate-400 hover:text-blue-600"
                          title="Resend leave notification">
                          <Mail className="h-3.5 w-3.5" />
                        </button>
                      )}
                      {canEdit && l.status === "pending" && onRedact && (
                        <button onClick={() => onRedact(l.id)}
                          className="p-1.5 hover:bg-red-50 rounded-sm transition-colors text-slate-500 hover:text-red-600" title="Redact request">
                          <XIcon className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* ── Rejection Dialog ── */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600"><MessageSquare className="h-5 w-5" /> Reject Leave Request</DialogTitle>
            <DialogDescription>Provide a reason for rejection. It will be sent to the employee via email.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-xs font-semibold text-slate-500 block mb-1.5">Rejection Reason</Label>
              <Select value={rejectReason} onValueChange={setRejectReason}>
                <SelectTrigger className="h-9"><SelectValue placeholder="Select a reason…" /></SelectTrigger>
                <SelectContent>{REJECTION_REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {rejectReason === "Other (custom reason)" && (
              <div>
                <Label className="text-xs font-semibold text-slate-500 block mb-1.5">Custom Reason</Label>
                <Textarea value={rejectCustom} onChange={(e) => setRejectCustom(e.target.value)}
                  placeholder="Type your reason for rejection…" rows={3} className="resize-none" />
              </div>
            )}
          </div>
          <DialogFooter>
            <button type="button" onClick={() => setRejectOpen(false)}
              className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-900 rounded-sm px-5 py-2.5 font-medium transition-colors text-sm">Cancel</button>
            <button type="button" onClick={handleReject}
              disabled={rejectSubmitting || !rejectReason || (rejectReason === "Other (custom reason)" && !rejectCustom.trim())}
              className="bg-red-600 hover:bg-red-700 text-white rounded-sm px-5 py-2.5 font-medium transition-colors text-sm">
              {rejectSubmitting ? "Rejecting…" : "Reject & Send Reason"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Amend Dialog ── */}
      <Dialog open={amendOpen} onOpenChange={setAmendOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-700"><Pencil className="h-5 w-5" /> Amend Leave Request</DialogTitle>
            <DialogDescription>Adjust approved dates. The balance is recalculated automatically.</DialogDescription>
          </DialogHeader>
          {amendLeave && (
            <div className="space-y-4 py-4">
              <div className="bg-slate-50 border border-slate-200 rounded-sm px-3 py-2.5 text-xs text-slate-600 space-y-1">
                <div><span className="font-semibold">Employee:</span> {amendLeave.user_name}</div>
                <div><span className="font-semibold">Type:</span> {amendLeave.leave_type}</div>
                <div><span className="font-semibold">Current:</span> {amendLeave.start_date} → {amendLeave.end_date} ({amendLeave.days} day(s))</div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-slate-500 block mb-1.5">New Start Date</label>
                  <input type="date" value={amendStart} onChange={(e) => setAmendStart(e.target.value)}
                    className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-500 transition-all text-sm" required />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500 block mb-1.5">New End Date</label>
                  <input type="date" value={amendEnd} onChange={(e) => setAmendEnd(e.target.value)}
                    className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-500 transition-all text-sm" required />
                </div>
              </div>
              {amendStart && amendEnd && (
                <div className={`rounded-sm px-3 py-2 text-xs font-medium border ${
                  amendedDays > amendLeave.days ? "bg-amber-50 border-amber-200 text-amber-700"
                  : amendedDays < amendLeave.days ? "bg-green-50 border-green-200 text-green-700"
                  : "bg-slate-50 border-slate-200 text-slate-600"}`}>
                  New duration: <strong>{amendedDays} day(s)</strong>
                  {amendedDays !== amendLeave.days && (
                    <span className="ml-1">({amendedDays > amendLeave.days ? "+" : ""}{amendedDays - amendLeave.days} from current)</span>
                  )}
                </div>
              )}
              <div>
                <label className="text-xs font-semibold text-slate-500 block mb-1.5">Note to Employee <span className="font-normal text-slate-400">(optional)</span></label>
                <Textarea value={amendComment} onChange={(e) => setAmendComment(e.target.value)}
                  placeholder="Reason for amendment…" rows={2} className="resize-none" />
              </div>
            </div>
          )}
          <DialogFooter>
            <button type="button" onClick={() => setAmendOpen(false)}
              className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-900 rounded-sm px-5 py-2.5 font-medium transition-colors text-sm">Cancel</button>
            <button type="button" onClick={handleAmendSubmit}
              disabled={amendSubmitting || !amendStart || !amendEnd || amendedDays <= 0}
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm px-5 py-2.5 font-medium transition-colors text-sm flex items-center gap-2">
              <Pencil className="h-4 w-4" />{amendSubmitting ? "Saving…" : "Save Amendment"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}